import { useState, useMemo, useCallback, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useSelector } from 'react-redux';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import {
  useTheme,
  Box,
  Typography,
  Chip,
  Tooltip,
  IconButton,
} from '@mui/material';
import { Edit } from '@mui/icons-material';
import useAuthCheck from 'hooks/useAuthCheck';
import {
  useGetScheduledFlightsQuery,
  useGetConflictsQuery,
  useCreateScheduledFlightMutation,
  useUpdateScheduledFlightMutation,
  useDeleteScheduledFlightsMutation,
  useGetAirportsQuery,
  useGetActiveAcTypesQuery,
} from 'features/apiSlice';
import { LAYOUT_HEIGHT } from 'state/constants';
import { buildPermissionMap, hasRight } from 'state/checkPermission';
import { getSelectedIds, isAnyRowSelected } from 'state/helpers';
import DataGridComponent from 'components/DataGridComponent';
import DeleteConfirm from 'components/DeleteConfirm';
import DaysChips from './DaysChips';
import FlightFormDialog from '../FlightFormDialog';
import ConflictDialog from './ConflictDialog';
import FilterDialog from './FilterDialog';
import { toMin, AC_TYPE_COLORS, AC_HEX } from './constants';


const Timetable = forwardRef(({ selectedSeasonId, seasons, onSelectionChange, onFilterChange }, ref) => {
  useAuthCheck();
  const user = useSelector((state) => state.user);
  const theme = useTheme();

  const permissionMap = useMemo(() => buildPermissionMap(user?.permissions), [user?.permissions]);
  const canUpdate = hasRight(permissionMap, 'SchedulePlanner', 'update', false);
  const canDelete = hasRight(permissionMap, 'SchedulePlanner', 'delete', false);

  const [filterDep, setFilterDep]   = useState('');
  const [filterArr, setFilterArr]   = useState('');
  const [filterAcType, setFilterAcType] = useState('');
  const [filterCarrier, setFilterCarrier] = useState('');
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 100 });
  const [rowSelectionModel, setRowSelectionModel] = useState({ type: 'include', ids: new Set() });

  const [filterOpen, setFilterOpen]       = useState(false);
  const [formDialog, setFormDialog]       = useState({ open: false, data: null });
  const [conflictDialog, setConflictDialog] = useState(false);
  const [confirmDelete, setConfirmDelete]  = useState(false);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [lastEntry, setLastEntry] = useState({ carrier: '', acType: '' });

  const { data: airportsData } = useGetAirportsQuery();
  const airports    = useMemo(() => airportsData || [], [airportsData]);
  const airportCodes = useMemo(
    () => airports.map((a) => a.iataCode).filter(Boolean).sort(),
    [airports],
  );

  const { data: acTypesData } = useGetActiveAcTypesQuery();
  const acTypes = useMemo(() => acTypesData || [], [acTypesData]);

  const selectedSeason = useMemo(
    () => seasons.find((s) => s._id === selectedSeasonId),
    [seasons, selectedSeasonId],
  );

  const queryParams = useMemo(() => ({
    seasonId: selectedSeasonId,
    dep:      filterDep   || undefined,
    arr:      filterArr   || undefined,
    acType:   filterAcType || undefined,
    carrier:  filterCarrier || undefined,
    page:     paginationModel.page + 1,
    limit:    paginationModel.pageSize,
  }), [
    selectedSeasonId, 
    filterDep, 
    filterArr, 
    filterAcType, 
    filterCarrier, 
    paginationModel,
  ]);

  const { data, isLoading, refetch } = useGetScheduledFlightsQuery(queryParams, { skip: !selectedSeasonId, refetchOnMountOrArgChange: true });
  const flights = useMemo(() => data?.data || [], [data]);
  const total   = data?.total || 0;

  useImperativeHandle(ref, () => ({
    openAddForm:    () => setFormDialog({ open: true, data: null }),
    deleteSelected: () => { if (isAnyRowSelected(rowSelectionModel, flights)) setConfirmDelete(true); },
    openFilter:     () => setFilterOpen(true),
    openConflicts:  () => setConflictDialog(true),
  }), [rowSelectionModel, flights]);

  const { data: conflictsData } = useGetConflictsQuery(selectedSeasonId, { skip: !selectedSeasonId });
  const conflictCount = conflictsData?.total || 0;

  const [createFlight] = useCreateScheduledFlightMutation();
  const [updateFlight] = useUpdateScheduledFlightMutation();
  const [deleteFlights] = useDeleteScheduledFlightsMutation();

  useEffect(() => {
    const handleKey = (e) => {
      if (formDialog.open || conflictDialog || confirmDelete) return;
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if ((e.ctrlKey || e.metaKey) && e.key === 'd') {
        e.preventDefault();
        const ids = getSelectedIds(rowSelectionModel, flights);
        if (!ids.length) return;
        const src = flights.find((f) => f._id === [...ids][0]);
        if (src) {
          const { _id, __v, createdAt, updatedAt, frequency, sectorType, blockTime, nextDay, ...rest } = src;
          setFormDialog({ open: true, data: { ...rest } });
        }
      } else if (e.key === 'Delete') {
        if (isAnyRowSelected(rowSelectionModel, flights) && canDelete) setConfirmDelete(true);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [
    formDialog.open, 
    conflictDialog, 
    confirmDelete, 
    canDelete, 
    rowSelectionModel, 
    flights,
  ]);

  const hasSelection   = isAnyRowSelected(rowSelectionModel, flights);
  const activeFilterCount = [filterDep, filterArr, filterAcType, filterCarrier].filter(Boolean).length;

  useEffect(() => {
    onSelectionChange?.(hasSelection, canDelete);
  }, [hasSelection, canDelete, onSelectionChange]);

  useEffect(() => {
    onFilterChange?.(activeFilterCount, conflictCount);
  }, [activeFilterCount, conflictCount, onFilterChange]);

  useEffect(() => {
    setPaginationModel((p) => ({ ...p, page: 0 }));
  }, [selectedSeasonId]);

  const clearFilters = () => {
    setFilterDep(''); setFilterArr(''); setFilterAcType(''); setFilterCarrier('');
    setPaginationModel((p) => ({ ...p, page: 0 }));
  };

  const acTypeColorMap = useMemo(() => {
    const types = [...new Set(flights.map((f) => f.acType).filter(Boolean))];
    return Object.fromEntries(types.map((t, i) => [t, AC_TYPE_COLORS[i % AC_TYPE_COLORS.length]]));
  }, [flights]);

  const acTypeHexMap = useMemo(() => {
    const types = [...new Set(flights.map((f) => f.acType).filter(Boolean))];
    return Object.fromEntries(types.map((t, i) => [t, AC_HEX[i % AC_HEX.length]]));
  }, [flights]);

  const handleSave = useCallback(async (values, { setSubmitting, resetForm }) => {
    try {
      if (formDialog.data?._id) {
        await updateFlight({ id: formDialog.data._id, ...values }).unwrap();
        toast.success('Flight updated.');
      } else {
        await createFlight(values).unwrap();
        toast.success('Flight created.');
      }
      const wasNew       = !formDialog.data?._id;
      const savedCarrier = values.carrier;
      const savedAcType  = values.acType;
      resetForm();
      setFormDialog({ open: false, data: null });
      if (wasNew) setLastEntry({ carrier: savedCarrier, acType: savedAcType });
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || 'Save failed.');
    } finally {
      setSubmitting(false);
    }
  }, [
    formDialog.data, 
    createFlight, 
    updateFlight, 
    refetch,
  ]);

  const handleDelete = useCallback(async () => {
    const ids = getSelectedIds(rowSelectionModel, flights);
    if (!ids.length) return;
    try {
      await deleteFlights(ids).unwrap();
      toast.success(`${ids.length} flight(s) deleted.`);
      setRowSelectionModel({ type: 'include', ids: new Set() });
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || 'Delete failed.');
    }
  }, [
    rowSelectionModel, 
    flights, 
    deleteFlights, 
    refetch,
  ]);

  const formInitialValues = useMemo(() => formDialog.data ? {
    seasonId:        formDialog.data.seasonId || selectedSeasonId,
    carrier:         formDialog.data.carrier  || '',
    flightNo:        formDialog.data.flightNo || '',
    dep: formDialog.data.dep || '', arr: formDialog.data.arr || '',
    std: formDialog.data.std || '', sta: formDialog.data.sta || '',
    daysOfOperation: formDialog.data.daysOfOperation || '1234567',
    acType:          formDialog.data.acType      || '',
    serviceType:     formDialog.data.serviceType || 'J',
    status:          formDialog.data.status      || 'planned',
    effectiveFrom:   formDialog.data.effectiveFrom ? format(new Date(formDialog.data.effectiveFrom), 'yyyy-MM-dd') : '',
    effectiveTo:     formDialog.data.effectiveTo   ? format(new Date(formDialog.data.effectiveTo),   'yyyy-MM-dd') : '',
    codeshare:       !!formDialog.data.codeshare,
    slotCoordinated: !!formDialog.data.slotCoordinated,
    notes:           formDialog.data.notes || '',
  } : {
    seasonId:        selectedSeasonId,
    carrier: lastEntry.carrier, flightNo: '',
    dep: '', arr: '', std: '', sta: '',
    daysOfOperation: '1234567',
    acType: lastEntry.acType, serviceType: 'J',
    status: 'planned',
    effectiveFrom: selectedSeason?.startDate ? format(new Date(selectedSeason.startDate), 'yyyy-MM-dd') : '',
    effectiveTo:   selectedSeason?.endDate   ? format(new Date(selectedSeason.endDate),   'yyyy-MM-dd') : '',
    codeshare: false, slotCoordinated: false,
    notes: '',
  }, [
    formDialog.data, 
    selectedSeasonId, 
    selectedSeason, 
    lastEntry,
  ]);

  const columns = useMemo(() => [
    {
      field: 'flightNo', 
      headerName: 'Flight', 
      width: 90,
      renderCell: ({ row }) => (
        <Typography 
          variant="body2" 
          sx={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {row.carrier}{row.flightNo}
        </Typography>
      ),
    },
    {
      field: 'route', 
      headerName: 'Route', 
      width: 100,
      renderCell: ({ row }) => (
        <Typography 
          variant="body2" 
          sx={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {row.dep} → {row.arr}
        </Typography>
      ),
    },
    {
      field: 'acType', 
      headerName: 'ACType', 
      width: 90,
      renderCell: ({ value }) => value ? <Chip label={value} size="small" color={acTypeColorMap[value] || 'default'} /> : '-',
    },
    { 
      field: 'std', 
      headerName: 'STD', 
      width: 70,
      renderCell: ({ row }) => (
        <Typography 
          variant="body2" 
          sx={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {row.std}
        </Typography> 
      ),
    },
    {
      field: 'sta', 
      headerName: 'STA', 
      width: 80,
      renderCell: ({ row }) => (
        <Typography 
          variant="body2" 
          sx={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {row.sta}{row.nextDay > 0 ? <sup style={{ color: "warning.main" }}>+{row.nextDay}</sup> : ''}
        </Typography>
      ),
    },
    {
      field: 'blockTime', 
      headerName: 'BlockTime', 
      width: 65,
      renderCell: ({ value }) => !value ? '-' : (
        <Typography 
          variant="body2" 
          fontFamily="monospace"
          sx={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {Math.floor(value / 60)}:{String(value % 60).padStart(2, '0')}
        </Typography>
      ),
    },
    { 
      field: 'daysOfOperation', 
      headerName: 'Days', 
      width: 200,
      renderCell: ({ value }) => <DaysChips daysOfOperation={value} /> 
    },
    { 
      field: 'frequency', 
      headerName: 'Freq', 
      width: 60,
      renderCell: ({ value }) => <Chip label={`${value}x`} size="small" variant="outlined" /> 
    },    
    {
      field: 'status', 
      headerName: 'Status', 
      width: 90,
      renderCell: ({ value }) => (
        <Chip label={value} size="small" color={value === 'active' ? 'success' : value === 'cancelled' ? 'error' : 'default'} />
      ),
    },
    { 
      field: 'effectiveFrom', 
      headerName: 'Eff From', 
      width: 95,
      renderCell: ({ value }) => value ? format(new Date(value), 'dd MMM yy') : '-' 
    },
    { 
      field: 'effectiveTo', 
      headerName: 'Eff To', 
      width: 95,
      renderCell: ({ value }) => value ? format(new Date(value), 'dd MMM yy') : '-' 
    },
    {
      field: 'timeline', 
      headerName: 'Timeline', 
      width: 140, 
      sortable: false,
      renderCell: ({ row }) => {
        const stdMin  = toMin(row.std);
        const blk     = row.blockTime || 90;
        const leftPct = (stdMin / 1440) * 100;
        const wPct    = Math.max(1, Math.min(100 - leftPct, (blk / 1440) * 100));
        const color   = acTypeHexMap[row.acType] || '#607d8b';
        const wrap    = stdMin + blk > 1440;

        return (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Box 
              sx={{ 
                width: '100%', 
                height: 18, 
                position: 'relative', 
                bgcolor: 'action.hover', 
                borderRadius: 0.5, 
                overflow: 'hidden',
              }}
            >
              <Box sx={{ position: 'absolute', left: 0, width: '25%', top: 0, bottom: 0, bgcolor: 'rgba(0,0,0,0.05)' }} />
              {[6, 12, 18].map((h) => (
                <Box key={h} sx={{ position: 'absolute', left: `${(h / 24) * 100}%`, top: 0, bottom: 0, width: 1, bgcolor: 'divider' }} />
              ))}
              <Box sx={{ position: 'absolute', left: `${leftPct}%`, width: `${wPct}%`, top: 2, bottom: 2, bgcolor: color, borderRadius: '2px', opacity: row.status === 'cancelled' ? 0.3 : 1 }} />
              {wrap && (
                <Box sx={{ position: 'absolute', left: 0, width: `${((stdMin + blk - 1440) / 1440) * 100}%`, top: 2, bottom: 2, bgcolor: color, borderRadius: '2px', opacity: 0.4 }} />
              )}
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'actions', 
      headerName: '', 
      width: 60, 
      sortable: false,
      renderCell: ({ row }) => (
        <Tooltip title="Edit">
          <IconButton 
            size="small" 
            onClick={() => setFormDialog({ open: true, data: row })} 
            disabled={!canUpdate}
          >
            <Edit />
          </IconButton>
        </Tooltip>
      ),
    },
  ], [
    acTypeColorMap, 
    acTypeHexMap, 
    canUpdate,
  ]);


  return (
    <Box>
      <Box
        display="grid"
        gridTemplateColumns="repeat(12, minmax(0, 1fr))"
        gap={2}
      >
        <Box
          sx={{
            gridColumn: 'span 12',
            height: LAYOUT_HEIGHT,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          {/* Grid */}
          <Box flex={1}>
            <DataGridComponent
              rows={flights}
              columns={columns}
              loading={isLoading}
              columnVisibilityModel={columnVisibilityModel}
              setColumnVisibilityModel={setColumnVisibilityModel}
              props={{
                rowCount: total,
                paginationMode: 'server',
                paginationModel,
                onPaginationModelChange: setPaginationModel,
                checkboxSelection: true,
                disableRowSelectionOnClick: true,
                rowSelectionModel,
                onRowSelectionModelChange: setRowSelectionModel,
                onRowDoubleClick: ({ row }) => canUpdate && setFormDialog({ open: true, data: row }),
                sx: {
                  border: 'none',
                  '& .MuiDataGrid-columnHeaders': { bgcolor: theme.palette.background.alt },
                  '& .MuiDataGrid-row': { cursor: canUpdate ? 'pointer' : 'default' },
                },
              }}
            />
          </Box>
        </Box>
      </Box>

      <FilterDialog
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        onChange={(field, value) => {
          if (field === 'dep') setFilterDep(value);
          else if (field === 'arr') setFilterArr(value);
          else if (field === 'acType') setFilterAcType(value);
          else if (field === 'carrier') setFilterCarrier(value);
          setPaginationModel((p) => ({ ...p, page: 0 }));
        }}
        onClear={clearFilters}
        filters={{ 
          dep: filterDep, 
          arr: filterArr, 
          acType: filterAcType, 
          carrier: filterCarrier 
        }}
        airportCodes={airportCodes}
        acTypes={acTypes}
      />

      <FlightFormDialog
        open={formDialog.open}
        onClose={() => setFormDialog({ open: false, data: null })}
        data={formDialog.data}
        initialValues={formInitialValues}
        seasons={seasons}
        onSave={handleSave}
      />

      <ConflictDialog
        open={conflictDialog}
        onClose={() => setConflictDialog(false)}
        conflictsData={conflictsData}
        conflictCount={conflictCount}
      />

      <DeleteConfirm
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={handleDelete}
        deleteType="bulk"
      />
    </Box>
  );
});

Timetable.displayName = 'Timetable';

export default Timetable;
